import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography, Card, Table, Alert, Button, Space, Segmented, Grid, message, theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FilePdfOutlined, InfoCircleOutlined, BulbOutlined, WarningOutlined } from '@ant-design/icons';
import { useShellTitle } from '../components/AppShell';
import { GUIDES } from '../content/guides';
import { Guide, GuideBlock } from '../content/guideTypes';
import { parseInline } from '../content/inline';
import { buildGuidePdf, guideFileName } from './guidePdf';
import { LOGO_SRC, loadImage } from './reportPdf';

const { useBreakpoint } = Grid;

/** Renders **bold** runs as <strong>; everything else is plain text. */
function Inline({ text }: { text: string }) {
  return (
    <>
      {parseInline(text).map((seg, i) =>
        seg.bold ? <strong key={i}>{seg.text}</strong> : <React.Fragment key={i}>{seg.text}</React.Fragment>,
      )}
    </>
  );
}

const CALLOUT_TYPE = { info: 'info', warning: 'warning', tip: 'success' } as const;
const CALLOUT_ICON = {
  info: <InfoCircleOutlined />,
  warning: <WarningOutlined />,
  tip: <BulbOutlined />,
};

function Block({ block }: { block: GuideBlock }) {
  switch (block.kind) {
    case 'subheading':
      return <Typography.Title level={5} style={{ marginTop: 20 }}>{block.text}</Typography.Title>;
    case 'paragraph':
      return <Typography.Paragraph><Inline text={block.text} /></Typography.Paragraph>;
    case 'bullets':
      return (
        <ul style={{ paddingLeft: 22, marginBottom: 16 }}>
          {block.items.map((it, i) => <li key={i} style={{ marginBottom: 6 }}><Inline text={it} /></li>)}
        </ul>
      );
    case 'steps':
      return (
        <ol style={{ paddingLeft: 22, marginBottom: 16 }}>
          {block.items.map((it, i) => <li key={i} style={{ marginBottom: 6 }}><Inline text={it} /></li>)}
        </ol>
      );
    case 'table': {
      const columns: ColumnsType<Record<string, React.ReactNode>> = block.columns.map((col, i) => ({
        title: col,
        dataIndex: `c${i}`,
        key: `c${i}`,
        render: (v: string) => <Inline text={v} />,
      }));
      const data = block.rows.map((row, ri) => {
        const rec: Record<string, React.ReactNode> = { key: ri };
        row.forEach((cell, ci) => { rec[`c${ci}`] = cell; });
        return rec;
      });
      return (
        <div style={{ marginBottom: 16 }}>
          <Table
            columns={columns}
            dataSource={data as Record<string, React.ReactNode>[]}
            size="small"
            bordered
            pagination={false}
          />
          {block.caption && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{block.caption}</Typography.Text>
          )}
        </div>
      );
    }
    case 'callout':
      return (
        <Alert
          style={{ marginBottom: 16 }}
          type={CALLOUT_TYPE[block.tone]}
          icon={CALLOUT_ICON[block.tone]}
          showIcon
          message={block.title}
          description={<Inline text={block.text} />}
        />
      );
    default:
      return null;
  }
}

export default function UserGuide() {
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const { setTitle } = useShellTitle();
  const [guideId, setGuideId] = useState(GUIDES[0]?.id);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => { setTitle('Help'); return () => setTitle(''); }, [setTitle]);

  const guide: Guide | undefined = useMemo(
    () => GUIDES.find(g => g.id === guideId) ?? GUIDES[0],
    [guideId],
  );

  if (!guide) return <Alert type="info" message="No user guides are available yet." showIcon />;

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const doc = buildGuidePdf(guide, { logo: await loadImage(LOGO_SRC) });
      doc.save(guideFileName(guide));
    } catch (e) {
      message.error('Could not generate the PDF. Please try again.');
    } finally {
      setPdfBusy(false);
    }
  };

  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', maxWidth: 1180, margin: '0 auto' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {GUIDES.length > 1 && (
          <Segmented
            style={{ marginBottom: 16 }}
            value={guide.id}
            onChange={val => setGuideId(String(val))}
            options={GUIDES.map(g => ({ label: g.title, value: g.id }))}
          />
        )}

        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <Typography.Title level={2} style={{ marginBottom: 4 }}>{guide.title}</Typography.Title>
              <Typography.Text type="secondary">{guide.subtitle}</Typography.Text>
              <div style={{ marginTop: 6 }}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Version {guide.version} · Updated {guide.updated}
                </Typography.Text>
              </div>
            </div>
            <Button type="primary" icon={<FilePdfOutlined />} onClick={downloadPdf} loading={pdfBusy}>
              Download PDF
            </Button>
          </div>

          <Typography.Paragraph style={{ marginTop: 16, fontSize: 15, color: token.colorTextSecondary }}>
            <Inline text={guide.intro} />
          </Typography.Paragraph>

          {guide.sections.map(section => (
            <section key={section.id} id={section.id} style={{ scrollMarginTop: 16, marginTop: 28 }}>
              <Typography.Title
                level={4}
                style={{ borderBottom: `2px solid ${token.colorPrimary}`, paddingBottom: 6 }}
              >
                {section.title}
              </Typography.Title>
              {section.blocks.map((block, i) => <Block key={i} block={block} />)}
            </section>
          ))}
        </Card>
      </div>

      {screens.lg && (
        <aside style={{ position: 'sticky', top: 0, width: 220, flexShrink: 0 }}>
          <Card size="small" title="On this page">
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              {guide.sections.map(section => (
                <Button
                  key={section.id}
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto', textAlign: 'left', whiteSpace: 'normal' }}
                  onClick={() => scrollTo(section.id)}
                >
                  {section.title}
                </Button>
              ))}
            </Space>
          </Card>
        </aside>
      )}
    </div>
  );
}
